// src/admin/pages/PlanListPage.jsx — superadmin plan settings (route /admin/plans).
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminTable from '../components/AdminTable.jsx';
import PlanModal from './PlanModal.jsx';
import { adminListPlans } from '../adminApi.js';
import { formatCredits } from '../../utils/creditsFormat.js';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const btnStyle = (color = COLORS.accent) => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '8px 14px', borderRadius: RADII.controlInner, border: 'none',
  background: color, color: COLORS.surface, fontSize: '13px', fontWeight: 600,
  cursor: 'pointer', fontFamily: FONT_SANS,
});

export default function PlanListPage() {
  const { t } = useTranslation('admin');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPlans(await adminListPlans());
    } catch (err) {
      setError(err.message);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Every column degrades on its own: a field an older backend never sent shows
  // the "n.d." neutral chip rather than a blank or a crash.
  const dash = <span style={{ color: COLORS.textSecondary }}>—</span>;

  const columns = [
    { key: 'display_name', label: t('plans.columns.name') },
    { key: 'id', label: t('plans.columns.id'), width: '120px', render: (row) => (
      <code style={{ fontFamily: FONT_MONO, fontSize: '12px', color: COLORS.textSecondary }}>{row.id}</code>
    ) },
    {
      // The allowance enforcement actually reads. `enforces_limit: false` is an
      // unlimited plan, where a null allowance is legitimate — not missing data.
      key: 'monthly_product_credits', label: t('plans.columns.allowance'), width: '140px',
      render: (row) => (row.enforces_limit === false
        ? <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{t('plans.values.unlimited')}</span>
        : (row.monthly_product_credits != null ? formatCredits(row.monthly_product_credits) : dash)),
    },
    {
      // Legacy cost scale — kept only to explain pre-change periods. NOT what a
      // customer sees on /balance.
      key: 'monthly_credits', label: t('plans.columns.legacyCredits'), width: '140px',
      render: (row) => (row.monthly_credits != null
        ? <span style={{ color: COLORS.textSecondary }}>{formatCredits(row.monthly_credits)}</span>
        : dash),
    },
    {
      // period_days and renews only mean something together.
      key: 'period_days', label: t('plans.columns.window'), width: '190px',
      render: (row) => {
        if (row.period_days == null) return t('plans.values.calendarMonth');
        return row.renews === false
          ? t('plans.values.trialWindow', { n: row.period_days })
          : t('plans.values.rollingWindow', { n: row.period_days });
      },
    },
    {
      key: 'allowed_formats', label: t('plans.columns.formats'), width: '150px',
      render: (row) => (Array.isArray(row.allowed_formats) && row.allowed_formats.length
        ? <span title={row.allowed_formats.join(', ')}>{t('plans.values.nFormats', { count: row.allowed_formats.length })}</span>
        : t('plans.values.allFormats')),
    },
    {
      // Seats PER WORKSPACE — not max_tenants.
      key: 'max_users', label: t('plans.columns.seats'), width: '110px',
      render: (row) => (row.max_users != null ? row.max_users : t('plans.values.noCap')),
    },
    {
      // Workspaces PER LICENCE — a different entitlement from max_users.
      key: 'max_tenants', label: t('plans.columns.workspaces'), width: '130px',
      render: (row) => (row.max_tenants != null ? row.max_tenants : t('plans.values.noCap')),
    },
    {
      key: 'self_serve', label: t('plans.columns.selfServe'), width: '120px',
      render: (row) => (
        <span style={{ fontSize: '12px', color: row.self_serve ? SEMANTIC.verde.fg : COLORS.textSecondary }}>
          {row.self_serve ? t('plans.values.selfServeYes') : t('plans.values.selfServeNo')}
        </span>
      ),
    },
    {
      key: 'active', label: t('shared.status'), width: '100px',
      render: (row) => (
        <span style={{ color: row.active ? SEMANTIC.verde.fg : SEMANTIC.errore.fg, fontSize: '12px' }}>
          {row.active ? t('shared.active') : t('plans.inactive')}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1200px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{t('plans.title')}</h1>
          <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: 0 }}>
            {t('plans.subtitle')}
          </p>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true); }} style={btnStyle()}>{t('plans.create')}</button>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, padding: '24px', overflowX: 'auto' }}>
        <AdminTable
          columns={columns} rows={plans} loading={loading}
          emptyMessage={t('plans.empty')}
          getRowKey={(row) => row.id}
          onRowClick={(row) => { setEditing(row); setModalOpen(true); }}
        />
      </div>

      <PlanModal open={modalOpen} plan={editing} onClose={() => setModalOpen(false)} onSaved={load} />
    </div>
  );
}
